// utils/videoDB.js
import { openDB } from 'idb';

const DB_NAME = 'VideoStore';
const STORE_NAME = 'videos';

let dbInitialized = false;
let dbError = null;

export async function initDB() {
  try {
    if (dbError) {
      console.warn('IndexedDB previously failed, skipping initialization');
      return null;
    }

    const db = await openDB(DB_NAME, 2, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        }
      },
    });
    
    dbInitialized = true;
    return db;
  } catch (error) {
    console.error('Failed to initialize IndexedDB:', error);
    dbError = error;
    
    // Show user-friendly message
    if (error.name === 'QuotaExceededError') {
      console.warn('⚠️ Storage quota exceeded. Video caching disabled. Videos will stream directly.');
    } else {
      console.warn('⚠️ IndexedDB unavailable. Video caching disabled. Videos will stream directly.');
    }
    
    return null;
  }
}

export async function storeVideo(key, blob, metadata = {}) {
  try {
    const db = await initDB();
    if (!db) {
      console.warn('IndexedDB not available, skipping video storage');
      return null;
    }

    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const videoData = {
      blob,
      metadata: {
        storedAt: new Date().toISOString(),
        size: blob.size,
        type: blob.type,
        ...metadata,
      },
    };

    await store.put(videoData, key);
    await tx.done;
    return videoData;
  } catch (error) {
    console.error('Failed to store video in IndexedDB:', error);
    return null;
  }
}

// export async function getVideo(key) {
//   try {
    
//     const db = await initDB();
//     if (!db) return null;



//     return await db.get(STORE_NAME, key);
//   } catch (error) {
//     console.error('Failed to get video from IndexedDB:', error);
//     return null;
//   }
// }


export async function getVideo(key) {
  try {
    console.log("🔍 Getting video from IndexedDB with key:", key);
    
    const db = await initDB();
    if (!db) {
      console.warn("⚠️ IndexedDB not initialized");
      return null;
    }

    // Get the result from IndexedDB
    const result = await db.get(STORE_NAME, key);
    
    // Verify we got an exact match (not a fuzzy match)
    if (result) {
      // Get all keys to verify exact match
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const allKeys = await store.getAllKeys();
      
      // Check if the exact key exists
      const exactMatch = allKeys.some(k => k === key);
      
      if (!exactMatch) {
        console.warn("⚠️ Key found but not an exact match. Requested:", key, "Available keys:", allKeys);
        return null;
      }
      
      console.log("✅ Video found in IndexedDB (exact match):", {
        key,
        size: result.metadata?.size,
        storedAt: result.metadata?.storedAt,
        hasBlob: !!result.blob
      });
      
      return result;
    } else {
      console.log("❌ Video not found in IndexedDB for key:", key);
      
      // Debug: Show what keys are available
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const allKeys = await store.getAllKeys();
      console.log("📋 Available keys in IndexedDB:", allKeys);
      
      return null;
    }
  } catch (error) {
    console.error('Failed to get video from IndexedDB:', error);
    return null;
  }
}

export async function hasVideo(key) {
  try {
    const db = await initDB();
    if (!db) return false;
    
    // Get all keys and check for exact match
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const allKeys = await store.getAllKeys();
    
    // Return true only if exact key exists
    const exactMatch = allKeys.some(k => k === key);
    
    if (exactMatch) {
      console.log("✅ Video exists in IndexedDB (exact match):", key);
    } else {
      console.log("❌ Video does not exist for exact key:", key);
    }
    
    return exactMatch;
  } catch (error) {
    console.error('Failed to check video in IndexedDB:', error);
    return false;
  }
}

export async function deleteVideo(key) {
  try {
    const db = await initDB();
    if (!db) return;
    await db.delete(STORE_NAME, key);
  } catch (error) {
    console.error('Failed to delete video from IndexedDB:', error);
  }
}

function processDropboxUrl(url) {
  if (!url.includes('dropbox.com')) return url;

  try {
    const urlObj = new URL(url);

    // Convert to direct download URL
    urlObj.hostname = 'dl.dropboxusercontent.com';

    // Ensure dl=1 parameter is set
    urlObj.searchParams.delete('dl');

    return urlObj.toString();
  } catch (error) {
    console.warn('Failed to process Dropbox URL, using original:', error);
    return url;
  }
}

// ✅ Enhanced preload with per-file progress
export async function preloadAndStore(url, key, onProgress) {
  try {
    const db = await initDB();
    
    // If IndexedDB is not available, skip caching but don't fail
    if (!db) {
      console.warn(`IndexedDB not available, skipping cache for ${key}`);
      return {
        status: 'skipped',
        key,
        reason: 'IndexedDB unavailable',
      };
    }

    const existing = await getVideo(key);

    if (existing) {
      console.log(`${key} video already cached`);
      return {
        status: 'cached',
        key,
        size: existing.metadata.size,
        storedAt: existing.metadata.storedAt,
      };
    }

    console.log(`Preloading ${url}...`);
    let fetchUrl = url;
    if (url.includes('dropbox.com')) {
      fetchUrl = processDropboxUrl(url);
      console.log(`Converted Dropbox URL: ${url} -> ${fetchUrl}`);
    }

    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const contentLengthHeader = response.headers.get('content-length');
    const contentLength = contentLengthHeader ? parseInt(contentLengthHeader, 10) : null;

    const reader = response.body.getReader();
    let receivedLength = 0;
    let lastLoggedProgress = -1;
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      if (contentLength && onProgress) {
        const percent = Math.floor((receivedLength / contentLength) * 100);
        if (percent !== lastLoggedProgress) {
          lastLoggedProgress = percent;
          onProgress(percent);
        }
      }
    }

    const blob = new Blob(chunks, { type: response.headers.get('content-type') });
    console.log(`Download complete. Size: ${blob.size} bytes`);

    const storedData = await storeVideo(key, blob, {
      sourceUrl: url,
      fetchedAt: new Date().toISOString(),
    });

    if (!storedData) {
      console.warn(`Failed to cache ${key}, but download was successful`);
      return {
        status: 'downloaded',
        key,
        size: blob.size,
        type: blob.type,
        cached: false,
      };
    }

    console.log(`${key} video cached successfully`);
    return {
      status: 'success',
      key,
      size: blob.size,
      type: blob.type,
      storedAt: storedData.metadata.storedAt,
    };
  } catch (error) {
    console.error(`Failed to preload ${key}:`, error);
    throw error;
  }
}

// ✅ Sequential preload with overall progress
export async function preloadVideosSequentially(videoList, onOverallProgress) {
  const progressMap = new Map(); // key -> percent
  const total = videoList.length;
  const results = [];

  const updateOverallProgress = () => {
    const values = Array.from(progressMap.values());
    const sum = values.reduce((acc, val) => acc + val, 0);
    const average = values.length ? sum / total : 0;
    onOverallProgress && onOverallProgress(average / 100);
  };

  for (let i = 0; i < total; i++) {
    console.log("videoList[i]",videoList[i])
    const { url, key } = videoList[i];
    console.log("url",url , " key:",key)
    if (!url) {
      results.push({ key, status: 'skipped' });
      progressMap.set(key, 100); // skipped = 100%
      updateOverallProgress();
      continue;
    }

    try {
      const result = await preloadAndStore(url, key, percent => {
        progressMap.set(key, percent);
        updateOverallProgress();
        // console.log(`Progress for ${key}: ${percent}%`);
      });
      results.push(result);
    } catch (err) {
      results.push({ key, status: 'error', error: err });
      progressMap.set(key, 100); // consider errored as done
    }

    // Make sure we set 100% if video completes successfully
    progressMap.set(key, 100);
    updateOverallProgress();
  }

  return results;
}

// Utility to check storage usage
export async function getStorageUsage() {
  try {
    const db = await initDB();
    if (!db) {
      return {
        videoCount: 0,
        totalSize: 0,
        formattedSize: '0 Bytes',
        available: false,
      };
    }

    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const keys = await store.getAllKeys();

    let totalSize = 0;
    for (const key of keys) {
      const data = await store.get(key);
      if (data?.metadata?.size) {
        totalSize += data.metadata.size;
      }
    }

    return {
      videoCount: keys.length,
      totalSize,
      formattedSize: formatBytes(totalSize),
      available: true,
    };
  } catch (error) {
    console.error('Failed to get storage usage:', error);
    return {
      videoCount: 0,
      totalSize: 0,
      formattedSize: '0 Bytes',
      available: false,
      error: error.message,
    };
  }
}

function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat(bytes / Math.pow(k, i)).toFixed(dm) + ' ' + sizes[i];
}

// Clear all videos
export async function clearAllVideos() {
  try {
    const db = await initDB();
    if (!db) {
      console.warn('IndexedDB not available, nothing to clear');
      return;
    }
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await tx.objectStore(STORE_NAME).clear();
    await tx.done;
    console.log('All videos cleared from IndexedDB');
  } catch (error) {
    console.error('Failed to clear videos from IndexedDB:', error);
  }
}

// Lightweight video preloader for in-memory only
export const preloadVideoStorage = url => {
  return new Promise((resolve, reject) => {
    if (!url) return resolve();

    const video = document.createElement('video');
    video.src = url;
    video.preload = 'auto';
    video.onloadeddata = () => {
      console.log(`Preloaded: ${url}`);
      resolve();
    };
    video.onerror = e => {
      console.error(`Failed to preload: ${url}`, e);
      resolve(); // resolve anyway to avoid blocking
    };
  });
};
